'use client';

import { Button, cn } from '@shared/components/shadcn';
import ChevronRight from '@shared/icons/ChevronRight';
import Lopetto from '@shared/icons/Lopetto';
import { ROUTES } from '@shared/utils';
import { useCallback, useEffect, useId, useState } from 'react';

type LopettoMessageSource = 'lopetto-embed';
type LopettoMessageType = 'lopetto:close';

type LopettoClosePayload = {
  source: LopettoMessageSource;
  type: LopettoMessageType;
};

type HelpCentreChatProps = {
  label: string;
  'data-track-name'?: string;
};

const HELP_EMBED_HOSTNAME = new URL(ROUTES.HELP_CENTRE.CHAT).hostname;
const ALLOWED_HELP_PROTOCOLS = new Set(['https:', 'http:']);

function isAllowedHelpEmbedOrigin(origin: string): boolean {
  try {
    const { hostname, protocol } = new URL(origin);
    return hostname === HELP_EMBED_HOSTNAME && ALLOWED_HELP_PROTOCOLS.has(protocol);
  } catch {
    return false;
  }
}

function isLopettoCloseMessage(data: unknown): data is LopettoClosePayload {
  if (typeof data !== 'object' || data === null) {
    return false;
  }

  const payload = data as Partial<LopettoClosePayload>;
  return payload.source === 'lopetto-embed' && payload.type === 'lopetto:close';
}

function HelpCentreChat({ label, 'data-track-name': dataTrackName }: HelpCentreChatProps) {
  const [isOpen, setIsOpen] = useState(false);
  const chatPanelId = useId();
  const handleOpen = useCallback(() => setIsOpen(true), []);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (!isAllowedHelpEmbedOrigin(event.origin) || !isLopettoCloseMessage(event.data)) {
        return;
      }
      setIsOpen(false);
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  return (
    <div className="h-full w-full min-w-0">
      <Button
        type="button"
        size="l"
        variant="secondary"
        className="group flex h-full w-full min-w-0 items-center justify-between gap-4 rounded-lg px-4 py-3 paragraph-strong text-nowrap truncate dark:hover:bg-container-secondary"
        aria-expanded={isOpen}
        aria-controls={chatPanelId}
        data-testid="lopetto-chat-opener"
        data-track-name={dataTrackName}
        onClick={handleOpen}
      >
        <div className="flex h-10 w-10 shrink-0 flex-col items-center justify-center rounded-full bg-contact-pod-icon-background border border-contact-pod-icon-border group-hover:border-transparent">
          <Lopetto className="h-5 w-5 text-contact-pod-icon-foreground" />
        </div>
        <span className="paragraph-explanatory-strong">{label}</span>
        <ChevronRight size="regular" className="ml-auto h-5 w-5 shrink-0" />
      </Button>

      <div
        id={chatPanelId}
        className={cn(
          'fixed z-[9999] overflow-hidden bg-white shadow-[0_10px_30px_rgba(0,0,0,0.2)]',
          !isOpen && 'hidden',
          'max-[659px]:inset-0 max-[659px]:h-screen max-[659px]:w-full max-[659px]:rounded-none',
          'min-[660px]:bottom-6 min-[660px]:right-6 min-[660px]:h-[707px] min-[660px]:w-[430px] min-[660px]:rounded-[10px]'
        )}
        data-testid="lopetto-chat-panel"
      >
        <iframe
          className="h-full w-full border-0 bg-white"
          data-testid="lopetto-chat-iframe"
          src={ROUTES.HELP_CENTRE.CHAT}
          title="Customer Support Chat"
          loading="lazy"
          referrerPolicy="strict-origin-when-cross-origin"
          allow="clipboard-write"
        />
      </div>
    </div>
  );
}

export { HelpCentreChat };
